import { useMutation, UseMutationResult } from "@tanstack/react-query";

import {
  EmptyObject,
  RecaptchaAction,
  WiseOwlApiResponse,
  WiseOwlIngress,
  WiseOwlProps,
} from "@twiliointernal/org/twilio/e2-commons/shared/definitions";

import { types } from "./definitions";
import { WiseOwlApi } from "@twiliointernal/org/twilio/e2-commons/shared/api";
import { getRecaptchaToken } from "@twiliointernal/org/twilio/e2-commons/shared/utils";

const useSubmitFeedback = ({
  applicationId,
  ingress,
  stargateUngatedUserId,
  token,
}: WiseOwlProps): UseMutationResult<
  WiseOwlApiResponse<EmptyObject>,
  any,
  types.UseWiseOwlSubmitFeedbackArgs
> => {
  return useMutation({
    mutationFn: async (args: types.UseWiseOwlSubmitFeedbackArgs) => {
      const wiseOwlApi = new WiseOwlApi(ingress);
      const recaptchaToken = await getRecaptchaToken(RecaptchaAction.submit);
      const csrf = await wiseOwlApi.getCsrf();

      const { chatId, messageId, positive, feedback } = args;

      const bodyDefaults = {
        feedback,
        positive,
      };
      const headerDefaults = {
        "Content-type": "application/json",
      };

      switch (ingress) {
        case WiseOwlIngress.oscUngated: {
          return wiseOwlApi.ungatedCreateFeedback({
            ingress,
            chatId,
            messageId,
            body: {
              ...bodyDefaults,
              recaptchaToken,
              stargateUngatedUserId,
            },
            headers: {
              ...headerDefaults,
              "x-twilio-csrf": csrf,
            },
            requestOptions: {
              credentials: "include",
            },
          }) as Promise<WiseOwlApiResponse<EmptyObject>>;
        }
        case WiseOwlIngress.osc: {
          return wiseOwlApi.createFeedback({
            ingress,
            chatId,
            messageId,
            body: { ...bodyDefaults, recaptchaToken },
            headers: {
              ...headerDefaults,
              "x-twilio-csrf": csrf,
            },
            requestOptions: {
              credentials: "include",
            },
          }) as Promise<WiseOwlApiResponse<EmptyObject>>;
        }
        case WiseOwlIngress.console: {
          return wiseOwlApi.createFeedback({
            ingress,
            chatId,
            messageId,
            body: { ...bodyDefaults, recaptchaToken, applicationId },
            headers: {
              ...headerDefaults,
              "x-twilio-csrf": csrf,
            },
            requestOptions: {
              credentials: "include",
            },
          }) as Promise<WiseOwlApiResponse<EmptyObject>>;
        }
        case WiseOwlIngress.zendesk: {
          return wiseOwlApi.createFeedback({
            ingress,
            chatId,
            messageId,
            body: bodyDefaults,
            headers: {
              ...headerDefaults,
              "wozd-auth": token,
              "zendesk-user-id": "12345",
            },
          }) as Promise<WiseOwlApiResponse<EmptyObject>>;
        }
        case WiseOwlIngress.salesforce: {
          return wiseOwlApi.createFeedback({
            ingress,
            chatId,
            messageId,
            body: bodyDefaults,
            headers: {
              ...headerDefaults,
              "TODO-SF-HEADER": "TODO-SF-HEADER",
              "TODO-SF-SECOND-HEADER": "TODO-SF-SECOND-HEADER",
            },
            requestOptions: {
              credentials: "include",
            },
          }) as Promise<WiseOwlApiResponse<EmptyObject>>;
        }
      }
    },
  });
};

export default useSubmitFeedback;
